import type { Game } from '../out/client_web';

/// Bridges the editor host (the VSCode extension's webview) to the game client.
///
/// The client runs in an iframe, so the two sides can only talk over
/// postMessage. Requests are forwarded to the wasm client verbatim as JSON and
/// events are polled back out, which keeps the whole protocol defined in Rust —
/// this file only moves messages and turns DOM input into editor requests.

/// Marks messages from the host, so unrelated postMessage traffic is ignored.
const HOST_SOURCE = 'aether-editor-host';
/// Marks messages from the viewport back to the host.
const VIEWPORT_SOURCE = 'aether-editor-viewport';

/// A pointer that moves less than this between press and release is a click,
/// not a drag. Trackpads jitter by a pixel or two during a tap.
const CLICK_SLOP_PX = 4;

interface HostMessage {
    source: typeof HOST_SOURCE;
    /// An `EditorRequest`; see `client::editor::session`.
    request: unknown;
}

function isHostMessage(data: unknown): data is HostMessage {
    return (
        typeof data === 'object' &&
        data !== null &&
        (data as { source?: unknown }).source === HOST_SOURCE
    );
}

function modifiers(event: MouseEvent | WheelEvent | KeyboardEvent) {
    return {
        shift: event.shiftKey,
        ctrl: event.ctrlKey,
        alt: event.altKey,
        meta: event.metaKey,
    };
}

/// `MouseEvent.buttons` is a bitmask, not the button that caused the event.
function buttons(event: MouseEvent) {
    return {
        left: (event.buttons & 1) !== 0,
        right: (event.buttons & 2) !== 0,
        middle: (event.buttons & 4) !== 0,
    };
}

export function startEditorBridge(game: Game, canvas: HTMLCanvasElement): () => void {
    // The browser can now open its own native Yakui editor. Only the VSCode
    // webview host should activate this legacy postMessage input bridge;
    // otherwise its pointer capture steals clicks from the in-client panels.
    let hostActive = false;
    const post = (message: object) => {
        window.parent?.postMessage({ source: VIEWPORT_SOURCE, ...message }, '*');
    };

    const send = (request: object) => {
        try {
            game.editor_request(JSON.stringify(request));
        } catch (e) {
            console.error('[aether] editor request failed', e);
        }
    };

    // The canvas is sized in physical pixels, and so is everything the editor
    // camera works in, so DOM coordinates are scaled to match.
    const scale = () => window.devicePixelRatio || 1;
    const position = (event: MouseEvent): [number, number] => [
        event.offsetX * scale(),
        event.offsetY * scale(),
    ];

    const onHostMessage = (event: MessageEvent) => {
        if (!isHostMessage(event.data)) {
            return;
        }
        hostActive = true;
        send(event.data.request as object);
    };
    window.addEventListener('message', onHostMessage);

    // --- Viewport input ---
    //
    // Input is captured whenever the host has enabled editor mode. Outside it,
    // events fall through to the game's own handlers untouched.

    let pressOrigin: { x: number; y: number } | null = null;
    let dragged = false;

    const pointerPayload = (event: PointerEvent) => ({
        position: position(event),
        delta: [event.movementX * scale(), event.movementY * scale()],
        buttons: buttons(event),
        modifiers: modifiers(event),
    });

    const onPointerDown = (event: PointerEvent) => {
        if (!hostActive || !game.editor_enabled()) {
            return;
        }
        event.preventDefault();
        canvas.setPointerCapture(event.pointerId);
        pressOrigin = { x: event.clientX, y: event.clientY };
        dragged = false;
        // A press is what can grab a gizmo axis, so it is reported separately
        // from a move.
        send({ type: 'pointerDown', event: pointerPayload(event) });
    };

    const onPointerMove = (event: PointerEvent) => {
        if (!hostActive || !game.editor_enabled()) {
            return;
        }
        if (pressOrigin) {
            const travelled =
                Math.abs(event.clientX - pressOrigin.x) + Math.abs(event.clientY - pressOrigin.y);
            if (travelled > CLICK_SLOP_PX) {
                dragged = true;
            }
        }
        send({ type: 'pointer', event: pointerPayload(event) });
    };

    const onPointerUp = (event: PointerEvent) => {
        if (!hostActive || !game.editor_enabled()) {
            return;
        }
        event.preventDefault();
        if (canvas.hasPointerCapture(event.pointerId)) {
            canvas.releasePointerCapture(event.pointerId);
        }
        send({ type: 'pointerUp', event: pointerPayload(event) });

        // A release only selects if the pointer stayed put. `buttons` has
        // already cleared the released button by now, so it is reconstructed
        // from `button` instead.
        if (!dragged) {
            send({
                type: 'click',
                event: {
                    position: position(event),
                    delta: [0, 0],
                    buttons: {
                        left: event.button === 0,
                        middle: event.button === 1,
                        right: event.button === 2,
                    },
                    modifiers: modifiers(event),
                },
            });
        }
        pressOrigin = null;
        dragged = false;
    };

    const onWheel = (event: WheelEvent) => {
        if (!hostActive || !game.editor_enabled()) {
            return;
        }
        // Without this the page scrolls and a pinch zooms the whole webview.
        event.preventDefault();
        send({
            type: 'wheel',
            event: {
                delta: [event.deltaX, event.deltaY],
                // Legacy field, but the most reliable way to tell a wheel notch
                // from a trackpad swipe; the classifier in Rust relies on it.
                wheelDeltaY: (event as WheelEvent & { wheelDeltaY?: number }).wheelDeltaY ?? 0,
                deltaMode: event.deltaMode,
                modifiers: modifiers(event),
            },
        });
    };

    // Right-drag is a navigation gesture, not a context menu.
    const onContextMenu = (event: Event) => {
        if (hostActive && game.editor_enabled()) {
            event.preventDefault();
        }
    };

    const onKeyDown = (event: KeyboardEvent) => {
        if (!hostActive || !game.editor_enabled()) {
            return;
        }
        if (event.code === 'BracketLeft' || event.code === 'BracketRight') {
            event.preventDefault();
            send({ type: 'resizeBrush', factor: event.code === 'BracketLeft' ? 0.8 : 1.25 });
            return;
        }
        if (event.code === 'Escape') {
            event.preventDefault();
            send({ type: 'cancelTool' });
            return;
        }
        // Numpad view shortcuts and framing, as in Blender.
        const views: Record<string, string> = {
            Numpad1: event.ctrlKey ? 'back' : 'front',
            Numpad3: event.ctrlKey ? 'left' : 'right',
            Numpad7: event.ctrlKey ? 'bottom' : 'top',
        };
        if (views[event.code]) {
            event.preventDefault();
            send({ type: 'snapView', view: views[event.code] });
            return;
        }
        if (event.code === 'NumpadDecimal' || event.code === 'Period') {
            event.preventDefault();
            send({ type: 'frameSelection' });
            return;
        }

        // Undo and redo, on whichever modifier this platform uses. Shift+Z
        // redoes, as it does in the editor this viewport sits inside.
        if ((event.metaKey || event.ctrlKey) && event.code === 'KeyZ') {
            event.preventDefault();
            send({ type: event.shiftKey ? 'redo' : 'undo' });
            return;
        }
        // Bare keys from here on: a modifier means the browser or the host is
        // being addressed, not the viewport.
        if (event.metaKey || event.ctrlKey || event.altKey) {
            return;
        }

        // What a drag on the gizmo does, on the keys every 3D tool uses.
        const modes: Record<string, string> = {
            KeyW: 'translate',
            KeyE: 'rotate',
            KeyR: 'scale',
        };
        if (modes[event.code]) {
            event.preventDefault();
            send({ type: 'setGizmoMode', mode: modes[event.code] });
            return;
        }

        if (event.code === 'Delete' || event.code === 'Backspace') {
            event.preventDefault();
            send({ type: 'deleteSelection' });
        }
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('keydown', onKeyDown);

    // --- Event pump ---
    //
    // Events are polled rather than pushed because the wasm client has no way
    // to call back into JS mid-tick.

    let frame = 0;
    // A panic inside the wasm client leaves it permanently borrowed, so every
    // later call throws. Give up rather than log once per frame forever.
    const MAX_CONSECUTIVE_FAILURES = 10;
    let failures = 0;

    const pump = () => {
        frame = window.requestAnimationFrame(pump);
        if (!hostActive) return;
        let json: string;
        try {
            json = game.take_editor_events();
            failures = 0;
        } catch (e) {
            if (++failures <= MAX_CONSECUTIVE_FAILURES) {
                console.error('[aether] could not read editor events', e);
                if (failures === MAX_CONSECUTIVE_FAILURES) {
                    console.error('[aether] editor bridge giving up; the client is not responding');
                    window.cancelAnimationFrame(frame);
                }
            }
            return;
        }
        if (json === '[]') {
            return;
        }
        try {
            const events = JSON.parse(json);
            if (events.length > 0) {
                post({ events });
            }
        } catch (e) {
            console.error('[aether] malformed editor events', json, e);
        }
    };
    frame = window.requestAnimationFrame(pump);

    // The host may have loaded before or after the client; announcing on start
    // covers the case where the host was already waiting.
    post({ ready: true });

    return () => {
        window.cancelAnimationFrame(frame);
        window.removeEventListener('message', onHostMessage);
        window.removeEventListener('keydown', onKeyDown);
        canvas.removeEventListener('pointerdown', onPointerDown);
        canvas.removeEventListener('pointermove', onPointerMove);
        canvas.removeEventListener('pointerup', onPointerUp);
        canvas.removeEventListener('pointercancel', onPointerUp);
        canvas.removeEventListener('wheel', onWheel);
        canvas.removeEventListener('contextmenu', onContextMenu);
    };
}
